/**
 * Checks if app is in an iframe - Mainly for the proxy implementation
 * @returns boolean
 */
export const isInIframe = (): boolean => {
  return !!window.ATARIM;
};

/**
 * Checks if the app is running in a Chrome extension
 * @returns boolean
 */
export const isInChromeExtension = () => {
  return !!window.chrome && !!chrome.runtime && !!chrome.runtime.id;
};

/**
 * Checks if the app is running in an embedded environment (e.g., iframe with embedded=true)
 * @returns boolean
 *
 */
export const isEmbedded = (): boolean => {
  return window.location.search.includes('embedded=true');
};

export const getNearestNodeInIframe = (path: string): Element | null => {
  const iframe = document.querySelector('iframe');
  if (!iframe?.contentDocument) return null;

  return iframe.contentDocument.querySelector(path) || null;
};

const DOMAIN_MAPPINGS = {
  'host.docker.internal': 'localhost',
  'atarimworker.io': 'atarimworker.dev',
} as const;

/**
 * Normalizes a URL string by replacing domain names
 * @param urlString The URL string to normalize
 * @returns The normalized URL string
 * @example
 * normalizeUrl('https://host.docker.internal/path'); // Returns 'https://localhost/path'
 */
export const normalizeUrl = (urlString: string, siteId?: string | null) => {
  try {
    // Pre-process the URL string to fix double ports before parsing
    const doublePortRegex = /:(\d+):(\d+)(\/|$)/;
    const doublePortMatch = urlString.match(doublePortRegex);

    if (doublePortMatch) {
      // Replace the double port with a single port
      urlString = urlString.replace(
        doublePortRegex,
        `:${doublePortMatch[1]}$3`
      );
    }

    // ensure the URL starts with http:// or https://
    if (!/^https?:\/\//i.test(urlString)) {
      urlString = 'https://' + urlString;
    }

    const url = new URL(urlString);
    let hostname = url.hostname;

    // Apply all domain replacements
    Object.entries(DOMAIN_MAPPINGS).forEach(([from, to]) => {
      hostname = hostname.replace(from, to);
    });

    // Construct base URL without query parameters
    const normalizedUrl = `${url.protocol}//${hostname}${url.port ? ':' + url.port : ''}${url.pathname}`;

    // If siteId is provided, append it as a query parameter
    if (siteId) {
      const urlWithSiteId = new URL(normalizedUrl);
      urlWithSiteId.searchParams.append('siteId', siteId);
      urlWithSiteId.searchParams.append('atarimCollab', 'true');
      return urlWithSiteId.toString();
    }

    return normalizedUrl;
  } catch (error) {
    console.error(`Error normalizing URL: ${urlString}`, error);
    return urlString;
  }
};

/**
 * Formats a URL string with the host if it's missing
 * @param urlString The URL string to format
 * @returns The formatted URL string or null if the URL is invalid
 * @example
 * formatUrlWithHost('https://example.com/path'); // Returns 'https://example.com/path'
 */
export const formatUrlWithScheme = (
  urlString: string,
  secure = true
): string | null => {
  try {
    // Check if the URL starts with http:// or https://
    if (!/^https?:\/\//i.test(urlString)) {
      // If it's a shorthand path, prepend https://
      if (secure) {
        urlString = 'https://' + urlString;
      } else {
        urlString = 'http://' + urlString;
      }
    }

    // Create URL object to validate and normalize the URL
    const url = new URL(urlString);

    // Ensure the URL has a valid host
    if (!url.host) {
      throw new Error('Invalid host in URL');
    }

    // Return the complete URL string
    return url.toString();
  } catch (error) {
    console.error('Error formatting URL:', error);
    return null;
  }
};

/**
 * Parses a URL string and returns the host and path
 * @param urlString The URL string to parse
 * @returns An object with the host and path or null if the URL is invalid
 * @example getHostAndPath('https://example.com/path') // Returns { fullPath: 'https://example.com/path', host: 'example.com', path: '/path' }
 */
export const getHostAndPath = (urlString: string) => {
  try {
    const url = new URL(urlString);

    // Get host without port number
    const host = url.host.split(':')[0];

    // Get pathname without query parameters
    let path = url.pathname;

    // Normalize path by removing trailing slash if it exists (except for root path)
    if (path.length > 1 && path.endsWith('/')) {
      path = path.slice(0, -1);
    }

    const fullPath = formatUrlWithScheme(`${host}${path}`);
    if (!fullPath) {
      throw new Error(`Invalid URL: ${urlString}`);
    }

    return {
      fullPath,
      host,
      path: path === '/' ? '' : path,
    };
  } catch (error) {
    console.error(`Error parsing URL: ${urlString}`, error);
    return null;
  }
};

/*
 * Returns the URL of the current task
 */
export const getPageUrl = (): string | null => {
  try {
    const currentUrl = getHostAndPath(window.location.href)?.fullPath;

    if (!currentUrl) return window.location.href;
    // Early return for Chrome extension
    if (!window.ATARIM) {
      return currentUrl.replace(/([^:]\/)\/+/g, '$1');
    }

    // Check required dependencies
    const siteUrl = getHostAndPath(currentUrl);
    if (!siteUrl) return window.location.href;

    // Construct the URL based on available host
    const constructedUrl = window.ATARIM?.host
      ? `https://${window.ATARIM.host}${siteUrl?.path}`
      : siteUrl.fullPath;

    // Fix double slashes (preserve protocol://)
    return constructedUrl.replace(/([^:]\/)\/+/g, '$1');
  } catch (error) {
    console.error('Error getting task URL:', error);
    return null;
  }
};

/**
 * Return the url params as an object
 */
export const getUrlParams = (string?: string): URLSearchParams => {
  if (string) {
    return new URLSearchParams(string);
  }
  // If no string is provided, use the current window location
  return new URLSearchParams(window.location.search);
};

export async function getBrowserInfo(): Promise<{
  name: string;
  version: string;
  platform: string;
}> {
  if ('userAgentData' in navigator) {
    const userAgentData = navigator.userAgentData as any;
    try {
      const uaData = await userAgentData.getHighEntropyValues([
        'fullVersionList',
        'platform',
        'platformVersion',
      ]);

      // Filter out the fake "Not)A;Brand" entries
      const realBrand = uaData.fullVersionList.find(
        (brand: any) =>
          !brand.brand.includes('Not') && !brand.brand.includes('A;Brand')
      );

      if (realBrand) {
        return {
          name: realBrand.brand,
          version: realBrand.version,
          platform: uaData.platform,
        };
      }
    } catch (error) {
      console.warn('Client Hints failed:', error);
    }

    // Fallback to basic brands
    const realBrand = userAgentData.brands.find(
      (brand: any) =>
        !brand.brand.includes('Not') && !brand.brand.includes('A;Brand')
    );

    if (realBrand) {
      const platform = userAgentData.platform || getPlatformFromUA();
      return {
        name: realBrand.brand,
        version: realBrand.version,
        platform: platform,
      };
    }
  }

  return parseUserAgent();
}

function parseUserAgent(): {
  name: string;
  version: string;
  platform: string;
} {
  const ua = navigator.userAgent;
  const platform = getPlatformFromUA();

  if (ua.includes('Chrome/') && !ua.includes('Edg/')) {
    const version = ua.match(/Chrome\/(\d+\.\d+)/)?.[1] || 'Unknown Version';
    return {
      name: 'Chrome',
      version: version,
      platform: platform,
    };
  }

  if (ua.includes('Edg/')) {
    const version = ua.match(/Edg\/(\d+\.\d+)/)?.[1] || 'Unknown Version';
    return {
      name: 'Edge',
      version: version,
      platform: platform,
    };
  }

  if (ua.includes('Firefox/')) {
    const version = ua.match(/Firefox\/(\d+\.\d+)/)?.[1] || 'Unknown Version';
    return {
      name: 'Firefox',
      version: version,
      platform: platform,
    };
  }

  if (ua.includes('Safari/') && !ua.includes('Chrome/')) {
    const version = ua.match(/Version\/(\d+\.\d+)/)?.[1] || 'Unknown Version';
    return {
      name: 'Safari',
      version: version,
      platform: platform,
    };
  }

  return {
    name: 'Unknown Browser',
    version: 'Unknown Version',
    platform: platform,
  };
}

function getPlatformFromUA() {
  const ua = navigator.userAgent;

  if (ua.includes('Windows NT')) return 'Windows';
  if (ua.includes('Mac OS X')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';

  return 'Unknown Platform';
}

export const getDomPath = (el: Node): string | undefined => {
  if (!el || el.nodeType !== Node.ELEMENT_NODE) {
    return;
  }

  const selector = getChromeEnhancedDomPath(el as Element);
  return selector;
};

// export const getDomPath = (el: Node): string | undefined => {
//   if (!el || el.nodeType !== Node.ELEMENT_NODE) {
//     return;
//   }

//   const stack: { selector: string; useDirectChild: boolean }[] = [];
//   let isShadow: boolean = false;
//   let currentEl: Node = el;

//   while (currentEl.parentNode != null) {
//     const currentElement = currentEl as Element;
//     const selectorInfo = buildElementSelector(
//       currentElement,
//       currentEl.parentNode
//     );

//     if (isShadow) {
//       selectorInfo.selector += '::shadow';
//       isShadow = false;
//     }

//     stack.unshift(selectorInfo);
//     currentEl = currentEl.parentNode;

//     if (currentEl.nodeType === 11) {
//       isShadow = true;
//       currentEl = (currentEl as ShadowRoot).host;
//     }
//   }

//   stack.splice(0, 1); // removes html element

//   // Build the final selector with appropriate combinators
//   const selector = stack
//     .map((item, index) => {
//       if (index === 0) return item.selector;
//       const combinator = item.useDirectChild ? ' > ' : ' ';
//       return combinator + item.selector;
//     })
//     .join('');
//   console.log('selector', selector);
//   return selector;
// };

// CSS identifier escaping utility
function escapeCSSIdentifier(identifier: string): string {
  // Only escape characters that actually break CSS selectors
  // Be much more selective about what we escape
  return identifier
    .replace(/:/g, '\\:') // Escape colons (Tailwind responsive/state variants)
    .replace(/\./g, '\\.') // Escape dots (Tailwind decimal spacing)
    .replace(/\//g, '\\/') // Escape forward slashes
    .replace(/\[/g, '\\[') // Escape opening square brackets
    .replace(/\]/g, '\\]'); // Escape closing square brackets
  // Note: Don't escape parentheses or single quotes inside brackets
  // as they're valid inside attribute selectors and calc() functions
}

function buildElementSelector(
  element: Element,
  parent: Node
): { selector: string; useDirectChild: boolean } {
  const nodeName = element.nodeName.toLowerCase();
  let selector = nodeName;

  // Priority 1: Stable test identifiers - these should always use direct child
  const testId =
    element.getAttribute('data-testid') ||
    element.getAttribute('data-test-id') ||
    element.getAttribute('data-cy') ||
    element.getAttribute('data-qa');

  if (testId) {
    const attrName = element.hasAttribute('data-testid')
      ? 'data-testid'
      : element.hasAttribute('data-test-id')
        ? 'data-test-id'
        : element.hasAttribute('data-cy')
          ? 'data-cy'
          : 'data-qa';
    return {
      selector: `${nodeName}[${attrName}="${testId}"]`,
      useDirectChild: true,
    };
  }

  // Priority 2: ID (but validate it's not dynamic) - with escaping
  if (element.id && isStableId(element.id)) {
    return {
      selector: `${nodeName}#${escapeCSSIdentifier(element.id)}`,
      useDirectChild: true,
    };
  }

  // Priority 3: Stable semantic attributes
  const semanticSelectors = getSemanticSelectors(element);
  if (semanticSelectors.length > 0) {
    selector += semanticSelectors.join('');

    // Check if this combination is unique enough
    if (isUniqueInParent(element, parent, selector)) {
      return {
        selector,
        useDirectChild: shouldUseDirectChild(element, parent, selector),
      };
    }
  }

  // Priority 4: Stable classes (filter out dynamic ones) - with escaping
  const stableClasses = getStableClasses(element);
  if (stableClasses.length > 0) {
    selector += '.' + stableClasses.join('.');

    if (isUniqueInParent(element, parent, selector)) {
      return {
        selector,
        useDirectChild: shouldUseDirectChild(element, parent, selector),
      };
    }
  }

  // Priority 5: Stable data attributes
  const stableDataAttrs = getStableDataAttributes(element);
  stableDataAttrs.forEach((attr) => {
    selector += `[${attr.name}="${attr.value}"]`;
  });

  // Priority 6: Structural position with fallback
  const finalSelector = addPositionalSelector(element, parent, selector);
  return { selector: finalSelector, useDirectChild: true };
}

function isStableId(id: string): boolean {
  // Reject IDs that look auto-generated or temporary
  const unstablePatterns = [
    /^[0-9]+$/, // pure numbers
    /^[a-z0-9]{8,}$/, // long random strings
    /headlessui-/i, // UI library generated
    /radix-/i,
    /react-/i,
    /floating-ui/i,
    /-\d+$/, // ending with numbers
    /^tmp/i,
    /^temp/i,
    /uuid|guid/i,
  ];

  return !unstablePatterns.some((pattern) => pattern.test(id));
}

function getSemanticSelectors(element: Element): string[] {
  const selectors: string[] = [];

  // Stable semantic attributes in order of preference
  const semanticAttrs = [
    'name',
    'type',
    'role',
    'aria-label',
    'aria-labelledby',
    'title',
    'placeholder',
    'value',
  ];

  semanticAttrs.forEach((attrName) => {
    const value = element.getAttribute(attrName);
    if (value && value.length < 100 && !isTemporaryValue(value)) {
      selectors.push(`[${attrName}="${value}"]`);
    }
  });

  return selectors;
}

function getStableClasses(element: Element): string[] {
  if (!element.className || typeof element.className !== 'string') {
    return [];
  }

  const classes = element.className.trim().split(/\s+/).filter(Boolean);

  // Filter out unstable class patterns
  const unstablePatterns = [
    /^css-[a-z0-9]+/i, // CSS-in-JS generated
    /^[a-z0-9]{6,}$/, // Random hash-like classes
    /-[0-9]+$/, // Classes ending with numbers
    /^js-/, // JavaScript hooks (often change)
    /state|active|focus|hover/i, // State classes
    /loading|error|success/i, // Status classes
    /animate|transition/i, // Animation classes
    /^_[a-z0-9]/i, // CSS Modules with hash
    /floating-ui/i,
    /headlessui/i,
    /radix/i,
  ];

  const stableClasses = classes.filter(
    (cls) => !unstablePatterns.some((pattern) => pattern.test(cls))
  );

  // Prioritize semantic classes
  const semanticClasses = stableClasses.filter((cls) =>
    /^(btn|button|link|nav|menu|content|header|footer|sidebar|main)/.test(cls)
  );

  const finalClasses =
    semanticClasses.length > 0
      ? semanticClasses.slice(0, 2)
      : stableClasses.slice(0, 3);

  // Apply CSS escaping to all class names
  return finalClasses.map((cls) => escapeCSSIdentifier(cls));
}

function getStableDataAttributes(
  element: Element
): Array<{ name: string; value: string }> {
  const stableAttrs: Array<{ name: string; value: string }> = [];

  // Exclude patterns that indicate temporary/dynamic data
  const excludePatterns = [
    /atarim/i,
    /floating-ui/i,
    /headlessui/i,
    /radix/i,
    /react/i,
    /-id$/,
    /instance|uuid|guid/i,
    /timestamp|time/i,
    /index|position/i,
  ];

  Array.from(element.attributes)
    .filter((attr) => attr.name.startsWith('data-'))
    .filter(
      (attr) => !excludePatterns.some((pattern) => pattern.test(attr.name))
    )
    .filter(
      (attr) =>
        attr.value && attr.value.length < 50 && !isTemporaryValue(attr.value)
    )
    .slice(0, 2)
    .forEach((attr) => {
      stableAttrs.push({ name: attr.name, value: attr.value });
    });

  return stableAttrs;
}

function isTemporaryValue(value: string): boolean {
  const temporaryPatterns = [
    /^[0-9]+$/, // Pure numbers
    /^[a-f0-9]{8,}$/i, // Hex strings (likely IDs)
    /timestamp|uuid|guid/i,
    /^\d{10,}$/, // Timestamps
  ];

  return temporaryPatterns.some((pattern) => pattern.test(value));
}

function shouldUseDirectChild(
  element: Element,
  parent: Node,
  selector: string
): boolean {
  try {
    // Check if direct child selector works
    const directMatches = (parent as Element).querySelectorAll(
      `:scope > ${selector}`
    );
    if (directMatches.length === 1 && directMatches[0] === element) {
      return true;
    }

    // Check if descendant selector is necessary
    const descendantMatches = (parent as Element).querySelectorAll(
      `:scope ${selector}`
    );
    return !(
      descendantMatches.length === 1 && descendantMatches[0] === element
    );
  } catch (error) {
    // Log CSS selector errors for debugging
    console.warn('CSS selector error:', selector, error);
    return true; // Default to direct child if there's an error
  }
}

function isUniqueInParent(
  element: Element,
  parent: Node,
  selector: string
): boolean {
  try {
    // First try direct child selector (current behavior)
    const directMatches = (parent as Element).querySelectorAll(
      `:scope > ${selector}`
    );
    if (directMatches.length === 1 && directMatches[0] === element) {
      return true;
    }

    // If direct child fails, try descendant selector as fallback
    const descendantMatches = (parent as Element).querySelectorAll(
      `:scope ${selector}`
    );
    return descendantMatches.length === 1 && descendantMatches[0] === element;
  } catch (error) {
    // Log CSS selector errors for debugging
    console.warn('CSS selector error:', selector, error);
    return false;
  }
}

function addPositionalSelector(
  element: Element,
  parent: Node,
  baseSelector: string
): string {
  // Get all child elements (not just same tag name)
  const allSiblings = Array.from(parent.childNodes).filter(
    (node) => node.nodeType === Node.ELEMENT_NODE
  ) as Element[];

  // Get siblings of the same tag name for :nth-of-type
  const sameTagSiblings = allSiblings.filter(
    (el) => el.nodeName === element.nodeName
  );

  // If it's the only element of its type, no positional selector needed
  if (sameTagSiblings.length === 1) {
    return baseSelector;
  }

  const elementIndex = allSiblings.indexOf(element);
  const sameTagIndex = sameTagSiblings.indexOf(element);

  // Try :first-child or :last-child first (more stable than nth-child)
  if (elementIndex === 0) {
    return `${baseSelector}:first-child`;
  } else if (elementIndex === allSiblings.length - 1) {
    return `${baseSelector}:last-child`;
  }

  // Try :first-of-type or :last-of-type
  if (sameTagIndex === 0) {
    return `${baseSelector}:first-of-type`;
  } else if (sameTagIndex === sameTagSiblings.length - 1) {
    return `${baseSelector}:last-of-type`;
  }

  // Check if nth-child is more stable (fewer total siblings)
  // vs nth-of-type (fewer same-tag siblings)
  if (allSiblings.length <= sameTagSiblings.length * 1.5) {
    return `${baseSelector}:nth-child(${elementIndex + 1})`;
  } else {
    return `${baseSelector}:nth-of-type(${sameTagIndex + 1})`;
  }
}

export const isInShadowRoot = (): boolean => {
  try {
    const root = document.querySelector('script')?.getRootNode();
    return root instanceof ShadowRoot;
  } catch (e) {
    return false;
  }
};

export const copyToClipboard = async (text: string): Promise<void> => {
  // First try direct clipboard access
  try {
    await navigator.clipboard.writeText(text);
    return;
  } catch (directError) {
    console.debug('Direct clipboard access failed:', directError);

    // If we're in an iframe, try postMessage approach
    if (isInIframe()) {
      return new Promise((resolve, reject) => {
        const handleMessage = (event: MessageEvent) => {
          if (event.data.type === 'CLIPBOARD_COPY_RESULT') {
            window.removeEventListener('message', handleMessage);
            if (event.data.success) {
              resolve();
            } else {
              reject(new Error(event.data.error));
            }
          }
        };

        window.addEventListener('message', handleMessage);
        window.parent.postMessage(
          {
            type: 'COPY_TO_CLIPBOARD',
            text: text,
          },
          '*'
        ); // Maybe we restrict this to our specific origins in production
      });
    }

    // If we're in a shadow root, try using a temporary input element
    try {
      // Create a temporary input element
      const input = document.createElement('input');
      input.setAttribute('type', 'text');
      input.setAttribute('value', text);
      input.style.position = 'absolute';
      input.style.left = '-9999px';

      // Handle shadow DOM
      const root = isInShadowRoot()
        ? document.querySelector('script')?.getRootNode()
        : document.body;

      if (!root) throw new Error('No valid root element found');

      root.appendChild(input);

      // Use the modern selection API
      input.select();
      input.setSelectionRange(0, text.length);

      // Try the clipboard API one more time with the selection
      await navigator.clipboard.writeText(input.value);

      // Clean up
      root.removeChild(input);
      return;
    } catch (fallbackError) {
      console.debug('Fallback clipboard access failed:', fallbackError);
    }

    throw new Error('All clipboard methods failed');
  }
};

/**
 * Chrome DevTools-style DOM path generator
 * Prioritizes structural stability over styling classes
 */

interface SelectorInfo {
  selector: string;
  needsParentContext: boolean;
}

export const getChromeStyleDomPath = (element: Element): string => {
  const path: string[] = [];
  let currentElement: Element | null = element;

  while (currentElement && currentElement.nodeType === Node.ELEMENT_NODE) {
    const selector = getChromeStyleSelector(currentElement);
    path.unshift(selector.selector);

    // If we found a unique selector that doesn't need parent context, stop here
    if (
      !selector.needsParentContext &&
      isGloballyUnique(currentElement, selector.selector)
    ) {
      break;
    }

    currentElement = currentElement.parentElement;

    // Stop at body or html
    if (
      currentElement?.tagName === 'BODY' ||
      currentElement?.tagName === 'HTML'
    ) {
      break;
    }
  }

  return path.join(' > ');
};

function getChromeStyleSelector(element: Element): SelectorInfo {
  const tagName = element.tagName.toLowerCase();

  // Priority 1: ID (Chrome's highest priority)
  if (element.id && isValidId(element.id)) {
    return {
      selector: `#${CSS.escape(element.id)}`,
      needsParentContext: false,
    };
  }

  // Priority 2: Unique stable attributes
  const stableAttrSelector = getStableAttributeSelector(element);
  if (
    stableAttrSelector &&
    isUniqueInDocument(element, `${tagName}${stableAttrSelector}`)
  ) {
    return {
      selector: `${tagName}${stableAttrSelector}`,
      needsParentContext: false,
    };
  }

  // Priority 3: Tag name with structural position
  const structuralSelector = getStructuralSelector(element);
  return {
    selector: structuralSelector,
    needsParentContext: true,
  };
}

function isValidId(id: string): boolean {
  // Chrome accepts most IDs but avoids obviously generated ones
  const generatedPatterns = [
    /^[0-9]+$/, // Pure numbers
    /^[a-f0-9]{8,}$/i, // Long hex strings
    /^react-/i, // React generated
    /^headlessui-/i, // Library generated
    /^radix-/i,
    /-\d{4,}$/, // Ends with long numbers
  ];

  return !generatedPatterns.some((pattern) => pattern.test(id));
}

function getStableAttributeSelector(element: Element): string | null {
  // Chrome prioritizes these attributes for uniqueness
  const stableAttributes = [
    'name',
    'data-testid',
    'data-test-id',
    'data-cy', // Cypress
    'data-qa',
    'aria-label',
    'type', // for inputs
    'role',
    'title',
  ];

  for (const attr of stableAttributes) {
    const value = element.getAttribute(attr);
    if (
      value &&
      value.length > 0 &&
      value.length < 100 &&
      !isGeneratedValue(value)
    ) {
      return `[${attr}="${CSS.escape(value)}"]`;
    }
  }

  return null;
}

function isGeneratedValue(value: string): boolean {
  const generatedPatterns = [
    /^[0-9]+$/, // Pure numbers
    /^[a-f0-9]{8,}$/i, // Hex IDs
    /^\d{10,}$/, // Timestamps
    /uuid|guid/i,
  ];

  return generatedPatterns.some((pattern) => pattern.test(value));
}

function getStructuralSelector(element: Element): string {
  const tagName = element.tagName.toLowerCase();
  const parent = element.parentElement;

  if (!parent) return tagName;

  // Get all sibling elements with the same tag name
  const siblings = Array.from(parent.children).filter(
    (child) => child.tagName.toLowerCase() === tagName
  );

  // If it's the only one of its type, no position needed
  if (siblings.length === 1) {
    return tagName;
  }

  const index = siblings.indexOf(element);

  // Use 1-based indexing like CSS
  if (index === 0) {
    return `${tagName}:first-of-type`;
  } else if (index === siblings.length - 1) {
    return `${tagName}:last-of-type`;
  } else {
    return `${tagName}:nth-of-type(${index + 1})`;
  }
}

function isUniqueInDocument(element: Element, selector: string): boolean {
  try {
    const matches = document.querySelectorAll(selector);
    return matches.length === 1 && matches[0] === element;
  } catch (error) {
    console.warn('Invalid selector:', selector, error);
    return false;
  }
}

function isGloballyUnique(element: Element, selector: string): boolean {
  try {
    // For ID selectors, they should be globally unique
    if (selector.startsWith('#')) {
      return isUniqueInDocument(element, selector);
    }

    // For attribute selectors, check if they're unique enough
    if (selector.includes('[')) {
      return isUniqueInDocument(element, selector);
    }

    return false;
  } catch (error) {
    return false;
  }
}

/**
 * Alternative implementation that more closely mimics Chrome's actual algorithm
 * This version builds from the element up to find the shortest unique path
 */
export const getChromeExactDomPath = (element: Element): string => {
  if (element.id && isValidId(element.id)) {
    return `#${CSS.escape(element.id)}`;
  }

  const path: string[] = [];
  let current: Element | null = element;

  while (current && current.nodeType === Node.ELEMENT_NODE) {
    let selector = getMinimalSelector(current);

    // Build potential path
    const currentPath = [selector, ...path].join(' > ');

    // Check if this path is unique
    if (isUniqueInDocument(current, currentPath)) {
      return currentPath;
    }

    path.unshift(selector);
    current = current.parentElement;

    // Stop at body
    if (current?.tagName === 'BODY') {
      break;
    }
  }

  return path.join(' > ');
};

function getMinimalSelector(element: Element): string {
  const tagName = element.tagName.toLowerCase();

  // Try stable attribute first
  const attrSelector = getStableAttributeSelector(element);
  if (attrSelector) {
    return `${tagName}${attrSelector}`;
  }

  // Fall back to structural position
  return getStructuralSelector(element);
}

/**
 * Enhanced version that handles special cases Chrome considers
 */
export const getChromeEnhancedDomPath = (element: Element): string => {
  // Handle special cases
  if (element.tagName === 'HTML') return 'html';
  if (element.tagName === 'BODY') return 'body';

  // Check for iframe context
  if (window !== window.top) {
    // In iframe, prefix might be needed
    console.warn('Element is in iframe context');
  }

  // Use the exact Chrome algorithm
  return getChromeExactDomPath(element);
};

export const isHTMLEmpty = (html: string): boolean => {
  if (!html || !html.trim()) return true;

  // Create a temporary DOM element to parse the HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Get the text content (this strips all HTML tags)
  const textContent = tempDiv.textContent || tempDiv.innerText || '';
  // Check if the text content is empty or just whitespace
  return !textContent.trim();
};
